import React from 'react';
import { Button, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Typography, notification } from 'antd';
import { PricingAPI } from '../../../controller/PricingAPI';

// 與後端 config/permissions.php 的角色代碼一致
const ROLE_OPTIONS = [
  { value: 'admin', label: '管理員' },
  { value: 'sales', label: '業務' },
];

function inviteUrl(token) {
  // 前端使用 HashRouter，連結必須帶 #
  return `${window.location.origin}${window.location.pathname}#/invite/${token}`;
}

export default function InvitationPanel() {
  const [form] = Form.useForm();
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [modalVisible, setModalVisible] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  // navigator.clipboard 在非 HTTPS 下不存在，複製失敗時把連結攤開讓使用者自己選取
  const [fallbackUrl, setFallbackUrl] = React.useState(null);

  const refresh = React.useCallback(() => {
    setLoading(true);
    PricingAPI.invitations.index()
      .then((response) => setRows(response.data))
      .catch((error) => notification.error({
        message: '讀取邀請清單失敗',
        description: error?.response?.data?.message || '請稍後再試',
      }))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const handleCopy = async (token) => {
    const url = inviteUrl(token);
    try {
      if (!navigator.clipboard) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(url);
      notification.success({ message: '連結已複製', description: '貼給對方即可，7 天內有效' });
    } catch (e) {
      // 非 HTTPS 環境沒有 clipboard API，不能無聲失敗
      setFallbackUrl(url);
    }
  };

  const handleCreate = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }

    setSubmitting(true);
    try {
      await PricingAPI.invitations.store(values);
      notification.success({ message: '邀請已建立', description: '請複製連結傳給對方' });
      form.resetFields();
      setModalVisible(false);
      refresh();
    } catch (error) {
      notification.error({
        message: '建立失敗',
        description: error?.response?.data?.message || '請確認欄位內容',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = (id) => {
    PricingAPI.invitations.destroy(id)
      .then(() => { notification.info({ message: '邀請已取消' }); refresh(); })
      .catch((error) => notification.error({
        message: '取消失敗',
        description: error?.response?.data?.message || '請稍後再試',
      }));
  };

  const columns = [
    { title: 'E-mail', dataIndex: 'email', key: 'email' },
    { title: '角色', dataIndex: 'role_label', key: 'role_label' },
    { title: '邀請人', dataIndex: 'invited_by_name', key: 'invited_by_name', render: (v) => v || '-' },
    {
      title: '到期時間',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (value) => new Date(value).toLocaleString(),
    },
    {
      title: '狀態',
      dataIndex: 'expired',
      key: 'expired',
      render: (expired) => (expired
        ? <Tag color="red">已過期</Tag>
        : <Tag color="green">有效</Tag>),
    },
    {
      title: '操作',
      dataIndex: 'id',
      key: 'id',
      render: (id, record) => (
        <Space>
          {!record.expired && (
            <Typography.Link onClick={() => handleCopy(record.token)}>複製連結</Typography.Link>
          )}
          <Popconfirm
            title={`確定要取消給 ${record.email} 的邀請？`}
            onConfirm={() => handleCancel(id)}
            okText="是"
            cancelText="否"
          >
            <Typography.Link>取消</Typography.Link>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ textAlign: 'right', marginBottom: 10 }}>
        <Button type="primary" onClick={() => { form.setFieldsValue({ role: 'sales' }); setModalVisible(true); }}>
          邀請
        </Button>
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        size="small"
        scroll={{ x: 'max-content' }}
      />

      <Modal
        title="邀請新成員"
        visible={modalVisible}
        onOk={handleCreate}
        onCancel={() => { form.resetFields(); setModalVisible(false); }}
        confirmLoading={submitting}
        okText="建立邀請"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            label="E-mail"
            name="email"
            rules={[
              { required: true, message: '請輸入 E-mail' },
              { type: 'email', message: 'E-mail 格式不正確' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="角色"
            name="role"
            rules={[{ required: true, message: '請選擇角色' }]}
          >
            <Select options={ROLE_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="請手動複製連結"
        visible={fallbackUrl !== null}
        onCancel={() => setFallbackUrl(null)}
        footer={null}
      >
        <p>這個瀏覽器環境不允許自動複製 (非 HTTPS 連線時會如此)，請自行選取以下連結：</p>
        <Input.TextArea value={fallbackUrl || ''} autoSize readOnly onFocus={(e) => e.target.select()} />
      </Modal>
    </div>
  );
}
